import {
  Stethoscope, Activity, ClipboardList, CheckCircle2,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";

const HERO_IMG = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1400&q=80";

const practices = [
  {
    icon: Stethoscope, color: "var(--accent-coral)",
    title: "Travel Nursing",
    tag: "Coast-to-coast deployment",
    body: "Provincially-licensed RNs and RPNs on 8 to 26-week contracts. From rural BC hospitals to downtown Toronto ICUs — fully credentialed, travel-package coordinated, ready in days, not weeks.",
    roles: [
      "Registered Nurses (RN) — ICU, ER, OR, L&D",
      "Med-Surg & Telemetry nurses",
      "Critical-care travel nurses",
      "LPNs / RPNs across all provinces",
      "Nurse Practitioners (NP) — primary care & specialty",
      "Float-pool and rapid-response RNs",
    ],
  },
  {
    icon: Activity, color: "var(--accent-teal)",
    title: "Allied Health",
    tag: "Therapists & technologists",
    body: "The clinical professionals who keep hospitals, clinics, and long-term-care facilities running. Provincially-credentialed, college-registered, and ready to step in for backfill, expansion, or short-notice gaps.",
    roles: [
      "Physical & Occupational Therapists",
      "Respiratory Therapists (RRT)",
      "Medical Laboratory Technologists (MLT)",
      "Medical Radiation Technologists (MRT)",
      "Speech-Language Pathologists",
      "Pharmacists & Pharmacy Technicians",
      "Sonographers & Diagnostic Imaging",
      "Paramedics (PCP / ACP)",
    ],
  },
  {
    icon: ClipboardList, color: "var(--accent-mint)",
    title: "Non-Clinical Staffing",
    tag: "Healthcare operations",
    body: "The backbone roles that determine whether your clinical staff can actually do their jobs. From scheduling and HIM to healthcare IT and compliance leadership.",
    roles: [
      "Medical Office Administrators & schedulers",
      "Medical billing & coding specialists",
      "Health Information Management (HIM)",
      "Healthcare IT — EHR, Cerner, Epic, Meditech",
      "Quality, accreditation & compliance leads",
      "Clinic managers & directors of care",
      "Patient-services coordinators",
      "Revenue-cycle analysts",
    ],
  },
];

export default function HealthcareStaffing() {
  return (
    <div data-testid="healthcare-staffing-page">
      <PageHero
        eyebrow="Healthcare Staffing"
        title={
          <>
            The workforce that keeps{" "}
            <span className="italic" style={{ color: "var(--accent-coral)" }}>Canadian healthcare</span> running.
          </>
        }
        subtitle="Hospitals, clinics, long-term care, community health, and private practice — Ashren places licensed, credentialed, ready-to-work healthcare talent across Canada. Travel nursing, allied health, and the non-clinical backbone roles that make care delivery possible."
        image={HERO_IMG}
        imageAlt="Canadian healthcare professionals"
        testId="healthcare-hero"
      />

      <section className="py-16 md:py-24" data-testid="healthcare-practices-list">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 space-y-5">
          {practices.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className="card grid md:grid-cols-12 gap-8 items-start" data-testid={`healthcare-practice-${i}`}>
                <div className="md:col-span-4">
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                       style={{ background: `${p.color}22`, color: p.color }}>
                    <Icon size={24} />
                  </div>
                  <div className="eyebrow mb-2" style={{ color: p.color }}>{p.tag}</div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight mb-3">{p.title}</h2>
                  <p className="text-[color:var(--muted)] leading-relaxed">{p.body}</p>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--muted)] mb-4">
                    Representative roles we place
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {p.roles.map((r) => (
                      <li key={r} className="flex items-start gap-2 text-[color:var(--primary-dark)]">
                        <CheckCircle2 size={18} className="shrink-0 mt-0.5" style={{ color: p.color }} />
                        <span className="text-sm">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white border-y border-[color:var(--border)]" data-testid="healthcare-trust">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="grid md:grid-cols-12 gap-8 items-start">
            <div className="md:col-span-5">
              <div className="eyebrow mb-3" style={{ color: "var(--accent-coral)" }}>Healthcare-first compliance</div>
              <h2 className="display-lg">Credentialed, verified, ready to work.</h2>
            </div>
            <div className="md:col-span-7 space-y-4 text-[color:var(--muted)] leading-relaxed">
              <p>
                Every clinical placement is verified for provincial college registration, immunizations,
                N95 fit-testing, vulnerable-sector and criminal-record checks, CPR/BLS, and any
                facility-specific orientation requirements before they walk in.
              </p>
              <p>
                We coordinate travel, housing, mileage, and licence-portability across provinces — so
                your clinical leaders can focus on patient care, not paperwork.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTABand
        title="Have an open shift or a vacant clinical lead?"
        subtitle="Whether it's a 13-week ICU travel contract or a director of patient services, brief us once — we return with verified, ready-to-work candidates."
        primaryLabel="Request a talent brief"
        primaryTo="/for-employers"
        secondaryLabel="I'm a clinician"
        secondaryTo="/for-candidates"
      />
    </div>
  );
}
